import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { TERRACOTTA, JOB_STATUS_LABEL, JOB_STATUS_PILL, formatDateTime, formatDueDate } from './jobs-shared.js';
import { JobNumberEditor } from './JobNumberEditor.jsx';

const LinkIssueBox = ({ jobId, linkedIssues, onLinked }) => {
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const path = search.trim() ? `/maintenance/issues?search=${encodeURIComponent(search.trim())}` : null;
  const { data: results } = useApi(path);
  const linkedIds = new Set((linkedIssues ?? []).map((i) => i.id));
  const candidates = (results ?? []).filter((i) => !linkedIds.has(i.id));

  const link = async (issueId) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/issues`, { issueId });
      setSearch('');
      onLinked?.();
    } catch (err) {
      alert(err.message ?? 'Failed to link issue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="rounded-md border border-dashed border-border-strong bg-bg-card p-3">
      <input
        type="text"
        value={search}
        onInput={(e) => setSearch(e.currentTarget.value)}
        placeholder="Search issues by title to link…"
        class="block w-full rounded-md border border-border-strong bg-bg-base px-3 py-1.5 text-sm placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      {candidates.length > 0 && (
        <div class="mt-2 space-y-1">
          {candidates.slice(0, 8).map((i) => (
            <button
              key={i.id}
              type="button"
              disabled={busy}
              onClick={() => link(i.id)}
              class="flex w-full items-center justify-between gap-2 rounded-md border border-border-subtle bg-bg-base px-2.5 py-1.5 text-left text-[13px] hover:border-border-strong hover:bg-bg-hover"
            >
              <span class="truncate text-text-body">{i.title}</span>
              <span class="shrink-0 font-mono text-[11px] text-text-tertiary">{i.ticketNumber}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const JobDetailPage = ({ id }) => {
  const { route } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: job, loading, error, refetch } = useApi(`/jobs/${id}`);
  const { data: users } = useApi('/jobs/assignable-users');

  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (job) {
      setForm({
        title: job.title,
        details: job.details ?? '',
        dueDate: job.dueDate ?? '',
        assigneeUserId: job.assigneeUserId ?? '',
        status: job.status,
      });
    }
  }, [job]);

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (error) return <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>;
  if (!job || !form) return null;

  const dirty = form.title !== job.title
    || form.details !== (job.details ?? '')
    || form.dueDate !== (job.dueDate ?? '')
    || String(form.assigneeUserId) !== String(job.assigneeUserId ?? '')
    || form.status !== job.status;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      await api.patch(`/jobs/${id}`, {
        title: form.title.trim(),
        details: form.details.trim() || null,
        dueDate: form.dueDate || null,
        assigneeUserId: form.assigneeUserId ? Number(form.assigneeUserId) : null,
        status: form.status,
      });
      refetch();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/complete`);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const unlinkIssue = async (issueId) => {
    try {
      await api.delete(`/jobs/${id}/issues/${issueId}`);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed to unlink');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete job "${job.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/jobs/${id}`);
      route('/jobs');
    } catch (err) {
      alert(err.message ?? 'Failed');
    }
  };

  return (
    <div>
      <a href="/jobs" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Jobs
      </a>

      <section class="mb-6 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: TERRACOTTA }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TERRACOTTA }}>
              Job <JobNumberEditor job={job} isAdmin={isAdmin} onSaved={refetch} />
            </span>
          </div>
          <h1 class="m-0 mb-2 text-[28px] leading-[1.18] tracking-tight text-text-primary">{job.title}</h1>
          <span class={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${JOB_STATUS_PILL[job.status]}`}>
            {JOB_STATUS_LABEL[job.status]}
          </span>
        </div>
        <div class="flex shrink-0 gap-2">
          {job.status !== 'done' && (
            <Button variant="primary" onClick={complete} disabled={busy}>Mark done</Button>
          )}
          {isAdmin && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
        </div>
      </section>

      <div class="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Details</p>
          <div class="space-y-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Title</p>
              <input
                type="text"
                value={form.title}
                onInput={set('title')}
                class="block w-full rounded-md border border-border-strong bg-bg-base px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Notes</p>
              <textarea
                rows={4}
                value={form.details}
                onInput={set('details')}
                placeholder="Job details…"
                class="block w-full resize-none rounded-md border border-border-strong bg-bg-base px-3 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Assignment</p>
          <div class="space-y-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Status</p>
              <select
                value={form.status}
                onChange={set('status')}
                class="block w-full rounded-md border border-border-strong bg-bg-base px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {Object.entries(JOB_STATUS_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Assignee</p>
              <select
                value={form.assigneeUserId}
                onChange={set('assigneeUserId')}
                class="block w-full rounded-md border border-border-strong bg-bg-base px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">Unassigned</option>
                {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
              </select>
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Due date</p>
              <input
                type="date"
                value={form.dueDate}
                onInput={set('dueDate')}
                class="block w-full rounded-md border border-border-strong bg-bg-base px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <p class="mt-1 text-[11px] text-text-muted">Shown as {formatDueDate(form.dueDate)}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-6 flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-card p-3">
        <p class="m-0 text-[12px] text-text-tertiary">
          Created by {job.createdByName ?? 'Unattributed'} · {formatDateTime(job.createdAt)}
        </p>
        <div class="flex items-center gap-2">
          {saveError && (
            <p class="m-0 text-[12px] font-semibold text-ink-magenta">
              {saveError instanceof ApiError ? saveError.message : (saveError.message ?? 'Failed')}
            </p>
          )}
          <Button variant="primary" onClick={save} disabled={busy || !dirty || !form.title.trim()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div class="mb-3 flex items-center justify-between">
        <h2 class="m-0 font-serif text-[18px] font-medium text-text-primary">Linked issues</h2>
        <button type="button" onClick={() => setLinking((v) => !v)} class="text-[12px] font-semibold text-brand">
          {linking ? 'Close' : '+ Link an issue'}
        </button>
      </div>

      {linking && (
        <div class="mb-3">
          <LinkIssueBox jobId={id} linkedIssues={job.linkedIssues} onLinked={() => { refetch(); }} />
        </div>
      )}

      {(!job.linkedIssues || job.linkedIssues.length === 0) ? (
        <p class="text-sm text-text-tertiary">No linked issues yet.</p>
      ) : (
        <div class="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
          {job.linkedIssues.map((i) => (
            <div key={i.id} class="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-2 last:border-b-0">
              <a href={`/maintenance/issues/${i.id}`} class="min-w-0 flex-1 truncate text-[13px] font-semibold text-text-primary hover:text-brand">
                {i.title}
              </a>
              <span class="shrink-0 font-mono text-[11px] text-text-tertiary">{i.ticketNumber}</span>
              <button type="button" onClick={() => unlinkIssue(i.id)} class="shrink-0 text-[11px] text-text-tertiary hover:text-ink-magenta">
                Unlink
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
